import type { UsuarioEquipe } from '@contefy/shared';
import { LogOut } from 'lucide-react';
import { createContext, useContext, useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import { LimiteErro } from '../componentes/LimiteErro';
import { Alerta, Botao, Cartao, Spinner } from '../componentes/ui';
import { equipeApi, sessao } from './api';

const UsuarioContexto = createContext<UsuarioEquipe | null>(null);
export const useUsuario = () => useContext(UsuarioContexto);

/** Casca do painel: autenticação (token do WordPress ou chave técnica), barra superior e navegação. */
export function LayoutEquipe() {
  const [usuario, setUsuario] = useState<UsuarioEquipe | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const entrar = async () => {
    setCarregando(true);
    setErro(null);
    try {
      setUsuario(await equipeApi.eu());
    } catch (e) {
      setUsuario(null);
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    sessao.capturarDaUrl();
    if (sessao.temCredencial()) entrar();
    else setCarregando(false);
  }, []);

  if (carregando) {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-500">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }

  if (!usuario) {
    return <Entrada erro={erro} aoEntrar={entrar} />;
  }

  return (
    <UsuarioContexto.Provider value={usuario}>
      <div className="min-h-screen bg-slate-50">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="mx-auto flex h-14 max-w-[1600px] items-center gap-4 px-4">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-marca-600 font-bold text-white">
                C
              </span>
              <div className="leading-tight">
                <div className="text-sm font-semibold text-slate-900">Contefy · Aberturas</div>
                <div className="text-[11px] text-slate-500">Societário</div>
              </div>
            </div>
            <nav className="ml-4 flex items-center gap-1 text-sm">
              <NavLink
                to="/equipe"
                end
                className={({ isActive }) =>
                  `rounded-lg px-3 py-1.5 ${isActive ? 'bg-marca-50 text-marca-900 font-medium' : 'text-slate-600 hover:bg-slate-100'}`
                }
              >
                Fila
              </NavLink>
              {usuario.perfil === 'administrador' && (
                <NavLink
                  to="/equipe/configuracoes"
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-1.5 ${isActive ? 'bg-marca-50 text-marca-900 font-medium' : 'text-slate-600 hover:bg-slate-100'}`
                  }
                >
                  Configurações
                </NavLink>
              )}
            </nav>
            <div className="ml-auto flex items-center gap-3 text-sm">
              <span className="hidden text-slate-700 sm:inline">
                {usuario.nome || usuario.sub}{' '}
                <span className="text-slate-400">· {usuario.perfil}</span>
              </span>
              <button
                type="button"
                className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-slate-600 hover:bg-slate-100"
                onClick={() => {
                  sessao.sair();
                  setUsuario(null);
                }}
              >
                <LogOut className="h-4 w-4" /> Sair
              </button>
            </div>
          </div>
        </header>
        <main className="mx-auto max-w-[1600px] px-4 py-5">
          <LimiteErro nome="página">
            <Outlet />
          </LimiteErro>
        </main>
      </div>
    </UsuarioContexto.Provider>
  );
}

function Entrada({ erro, aoEntrar }: { erro: string | null; aoEntrar: () => void }) {
  const [chave, setChave] = useState('');
  const [mostrarTecnico, setMostrarTecnico] = useState(false);
  return (
    <main className="mx-auto max-w-md p-4 sm:p-10">
      <Cartao>
        <h1 className="text-xl font-semibold">Painel de aberturas</h1>
        <p className="mt-2 text-slate-600">
          O acesso é pela Área do Colaborador do site: abra o card <b>Aberturas</b> e você entra
          aqui com o seu login do WordPress.
        </p>
        {erro && (
          <div className="mt-3">
            <Alerta tipo="erro">{erro}</Alerta>
          </div>
        )}
        <a
          href="https://contefy.com.br/?contefy_aberturas=painel"
          className="mt-4 inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-marca-600 px-4 text-white hover:bg-marca-700"
        >
          Entrar pela Área do Colaborador
        </a>
        <button
          type="button"
          className="mt-4 text-xs text-slate-500 underline"
          onClick={() => setMostrarTecnico((v) => !v)}
        >
          Acesso técnico
        </button>
        {mostrarTecnico && (
          <form
            className="mt-2 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              sessao.usarChaveApi(chave.trim());
              aoEntrar();
            }}
          >
            <input
              type="password"
              placeholder="Chave da API interna"
              value={chave}
              onChange={(e) => setChave(e.target.value)}
            />
            <Botao type="submit" variante="secundario">
              Entrar
            </Botao>
          </form>
        )}
      </Cartao>
    </main>
  );
}
